import React, { useState, useEffect } from 'react';
import {
  TextInput,
  Form,
  Modal,
  Loading,
  Select,
  SelectItem,
  DatePickerInput,
  DatePicker,
} from 'carbon-components-react';
import CustomButton from '../../custom-button/custom-btn.component';
import { useSelector, useDispatch } from 'react-redux';

import { selectCurrentUser } from '../../../redux/selectors/user.selectors';

import {
  selectCurrentAssetsLedgers,
  selectRevenueLedgerAccounts,
} from '../../../redux/selectors/chart-of-accounts.selector';
import {
  setPayments,
  removePayment,
} from '../../../redux/actions/revenue.actions';
import { newPayment, revisePayment } from '../../../api/revenue.service';
import { setLedgerAccounts } from '../../../redux/actions/chart-of-accounts.actions';
import { getLedgerAccounts } from '../../../api/chart-of-accounts.service';
import ComponentGuard from '../../component-guard/ComponentGuard';
import { today, getQueryString } from '../../../utils/common.util';
import { useDebounceState } from '../../../utils/useDebounceState';
import useSWR from 'swr';
import Combobox from '../../../components/combobox/Combobox';
import './receive-other-payment.styles.scss';
import { RenderIf } from '@/components/RenderIf';

const ReceiveOtherPaymentModal = ({
  openModal,
  paymentToRevise,
  showReceipt,
  cancelAction,
}) => {
  const dispatch = useDispatch();
  const currentUser = useSelector(selectCurrentUser);
  const currentAssets = useSelector(selectCurrentAssetsLedgers);
  const revenueLedgers = useSelector(selectRevenueLedgerAccounts);

  const [loading, setLoading] = useState(false);
  const [btnText, setBtnText] = useState('SUBMIT PAYMENT');
  const [heading, setHeading] = useState('Receive Other Payment');
  const [errorMessage, setErrorMessage] = useState(null);
  const [internalOpenModal, setInternalOpenModal] = useState(openModal);
  const [paymentDate, setPaymentDate] = useState(today);
  const [creditAccount, setCreditAccount] = useState('');
  const [debitAccount, setDebitAccount] = useState('');
  const [amount, setAmount] = useState('');
  const [paymentMode, setPaymentMode] = useState('');
  const [chequeNumber, setChequeNumber] = useState('');
  const [bankersDraftNumber, setBankersDraftNumber] = useState('');
  const [bankTransferNumber, setBankTransferNumber] = useState('');
  const [receivedFrom, setReceivedFrom] = useState('');
  const [description, setDescription] = useState('');
  const [studentId, setStudentId] = useState(null);

  const [searchStudent, setSearchStudent] = useDebounceState('');
  const { data: students, isLoading: isStudentsLoading } = useSWR(
    `student/list?status=Enrolled&${getQueryString({ query: searchStudent })}`,
  );

  useEffect(() => {
    setInternalOpenModal(openModal);
  }, [openModal]);

  useEffect(() => {
    getLedgerAccounts(currentUser?.authToken, 0, 2000).then((response) => {
      if (response.success) {
        dispatch(setLedgerAccounts(response.ledgerAccounts));
      }
    });
  }, [currentUser?.authToken, dispatch]);

  useEffect(() => {
    if (paymentToRevise) {
      setHeading('Revise Other Payment');
      setBtnText('REVISE PAYMENT');
      setStudentId(paymentToRevise.student?.id ?? null);
      setReceivedFrom(paymentToRevise.receivedFrom);
      setPaymentMode(paymentToRevise.paymentMode);
      setChequeNumber(paymentToRevise.chequeNumber);
      setDescription(paymentToRevise.description);
      setPaymentDate(paymentToRevise.paymentDate);
      setDebitAccount(paymentToRevise.debitAccount.id);
      setAmount(paymentToRevise.revenues[0].amountPaid);
      setCreditAccount(paymentToRevise.revenues[0].ledgerAccount.id);
    } else {
      setHeading('Receive Other Payment');
      setBtnText('SUBMIT PAYMENT');
      setStudentId(null);
      setReceivedFrom('');
      setPaymentMode('');
      setChequeNumber('');
      setDescription('');
      setPaymentDate(today);
      setDebitAccount('');
      setCreditAccount('');
      setAmount('');
    }
  }, [paymentToRevise]);

  const submitPayment = async () => {
    setLoading(true);
    setErrorMessage(null);
    setBtnText(null);

    const data = {
      paymentDate,
      paymentMode,
      chequeNumber,
      description,
      debitAccount: currentAssets.find((asset) => asset.id == debitAccount),
      receivedFrom,
      relatedStudentId: studentId ? Number(studentId) : undefined,
      revenues: [
        {
          ledgerAccount: revenueLedgers.find(
            (ledger) => ledger.id == creditAccount,
          ),
          amountPaid: parseFloat(amount),
        },
      ],
    };

    const response = await newPayment(currentUser.authToken, data);

    if (!response.success) {
      setLoading(false);
      setErrorMessage(response.message);
      setBtnText('SUBMIT PAYMENT');
      return;
    }

    dispatch(setPayments([response.payment]));
    setLoading(false);
    setBtnText('SUBMIT PAYMENT');
    setStudentId(null);
    setReceivedFrom('');
    setPaymentDate(today);
    setPaymentMode('');
    setChequeNumber('');
    setDescription('');
    setDebitAccount('');
    setCreditAccount('');
    setAmount('');
    showReceipt(response.payment);
  };

  const onRevisePayment = async () => {
    setLoading(true);
    setErrorMessage(null);
    setBtnText(null);

    const data = {
      id: paymentToRevise.id,
      paymentMode,
      chequeNumber,
      description,
      debitAccount: currentAssets.find((asset) => asset.id == debitAccount),
      receivedFrom,
      relatedStudentId: studentId ? Number(studentId) : undefined,
      revenues: [
        {
          ledgerAccount: revenueLedgers.find(
            (ledger) => ledger.id == creditAccount,
          ),
          amountPaid: parseFloat(amount),
        },
      ],
    };

    const response = await revisePayment(currentUser.authToken, data);

    if (!response.success) {
      setLoading(false);
      setErrorMessage(response.message);
      setBtnText('REVISE PAYMENT');
      return;
    }

    dispatch(setPayments([response.payment]));
    dispatch(removePayment(data.id));

    setLoading(false);
    setHeading('Receive Other Payment');
    setBtnText('SUBMIT PAYMENT');
    setStudentId(null);
    setReceivedFrom('');
    setPaymentDate(today);
    setPaymentMode('');
    setChequeNumber('');
    setDescription('');
    setDebitAccount('');
    setCreditAccount('');
    setAmount('');
    showReceipt(response.payment);
  };

  const selectedStudent = students?.find((s) => s.id === studentId) ?? null;

  return (
    <Modal
      id="receive-other-payment-modal"
      className="receive-other-payment-modal"
      hasScrollingContent
      passiveModal={true}
      open={internalOpenModal}
      modalHeading={heading}
      primaryButtonDisabled={false}
    >
      <div>
        <Form action="post">
          <div className="card-row-1">
            <ComponentGuard allowedRoles={['RevenueBackDatingAccess']}>
              <DatePicker
                dateFormat="Y-m-d"
                datePickerType="single"
                id="start-date-picker"
                maxDate={new Date().toISOString().split('T')[0]}
                onClose={(value) => {
                  if (value.length > 0) {
                    setPaymentDate(
                      [
                        value[0].getFullYear(),
                        ('0' + (value[0].getMonth() + 1)).slice(-2),
                        ('0' + value[0].getDate()).slice(-2),
                      ].join('-'),
                    );
                  }
                }}
              >
                <DatePickerInput
                  className="payment-date"
                  id="payment-date"
                  name="paymentDate"
                  value={paymentDate}
                  onChange={(e) => setPaymentDate(e.target.value)}
                  labelText="Payment Date"
                  pattern="d{1,2}/d{4}"
                  placeholder="YYYY-MM-DD"
                />
              </DatePicker>
            </ComponentGuard>

            <Select
              id="creditAccount"
              labelText="Credit Account (Source of Income)"
              onChange={(e) => setCreditAccount(e.target.value)}
              value={creditAccount}
            >
              <SelectItem
                selected={creditAccount === ''}
                disabled
                text="Select Account to Credit"
                value=""
              />
              {revenueLedgers
                .filter(
                  (ledger) =>
                    ledger.categoryDescription !== 'Student Fees' &&
                    ledger.categoryDescription !== 'Student Revenue',
                )
                .map((ledger, i) => (
                  <SelectItem
                    key={i}
                    selected={ledger.id == creditAccount}
                    text={ledger.description}
                    value={ledger.id}
                  />
                ))}
            </Select>

            <Select
              id="debitAccount"
              labelText="Debit Account (Cash or Bank Account)"
              onChange={(e) => setDebitAccount(e.target.value)}
              value={debitAccount}
            >
              <SelectItem
                selected={debitAccount === ''}
                disabled
                text="Select Account to Debit"
                value=""
              />
              {currentAssets
                .filter(
                  (currentAsset) =>
                    currentAsset.categoryDescription === 'Bank Accounts' ||
                    currentAsset.categoryDescription === 'Cash Accounts',
                )
                .map((currentAsset, i) => (
                  <SelectItem
                    key={i}
                    selected={currentAsset.id == debitAccount}
                    text={currentAsset.description}
                    value={currentAsset.id}
                  />
                ))}
            </Select>

            <TextInput
              labelText="Amount"
              type="number"
              step="0.01"
              value={amount}
              placeholder="Please enter amount"
              onChange={(e) => {
                setAmount(parseFloat(e.target.value) || '');
              }}
            />

            <Select
              id="payment"
              labelText="Payment Mode"
              onChange={(e) => setPaymentMode(e.target.value)}
              value={paymentMode}
            >
              <SelectItem
                selected={paymentMode === ''}
                disabled
                text="Select payment mode"
                value=""
              />
              <SelectItem
                selected={paymentMode === 'Cash'}
                text="Cash"
                value="Cash"
              />
              <SelectItem
                selected={paymentMode === 'Cheque'}
                text="Cheque"
                value="Cheque"
              />
              <SelectItem
                selected={paymentMode === 'Bank_Transfer'}
                text="Bank Transfer"
                value="Bank_Transfer"
              />
              <SelectItem
                selected={paymentMode === 'BankDraft'}
                text="Bank Draft"
                value="BankDraft"
              />
              <SelectItem
                selected={paymentMode === 'MobileMoney'}
                text="Mobile Money"
                value="MobileMoney"
              />
            </Select>

            <RenderIf condition={paymentMode === 'Cheque'}>
              <TextInput
                id="cheque-number"
                labelText="Cheque Number"
                value={chequeNumber}
                onChange={(e) => setChequeNumber(e.target.value)}
                placeholder="Enter Cheque Number"
              />
            </RenderIf>

            <RenderIf condition={paymentMode === 'BankDraft'}>
              <TextInput
                id="bankers-draft-number"
                labelText="Bankers Draft Number"
                value={bankersDraftNumber}
                onChange={(e) => setBankersDraftNumber(e.target.value)}
                placeholder="Enter Bankers Draft Number"
              />
            </RenderIf>
            <RenderIf condition={paymentMode === 'Bank_Transfer'}>
              <TextInput
                id="bank-transfer-number"
                labelText="Bank Transfer Number"
                value={bankTransferNumber}
                onChange={(e) => setBankTransferNumber(e.target.value)}
                placeholder="Enter Bank Transfer Number"
              />
            </RenderIf>

            <TextInput
              labelText="Received From"
              value={receivedFrom}
              placeholder="Who are you receiving the payment from ?"
              onChange={(e) => setReceivedFrom(e.target.value)}
            />

            <Combobox
              options={students ?? []}
              label="Student (optional)"
              customStyles={{ width: '100%' }}
              value={selectedStudent}
              onChange={(option) => {
                setStudentId(option ? option.id : null);
              }}
              onSearch={setSearchStudent}
              onClear={() => {
                setStudentId(null);
                setSearchStudent('');
              }}
              loading={isStudentsLoading}
              renderSelected={(option) =>
                option
                  ? `${option.firstName} ${option.lastName} - ${option.classDescription}`
                  : ''
              }
              renderOption={(option) =>
                `${option.firstName} ${option.lastName} - ${option.classDescription}`
              }
              labelKey="fullName"
              valueKey="id"
              searchPlaceholder="Search students..."
              placeholder="Select a student (optional)"
            />

            <TextInput
              labelText="Description"
              value={description}
              placeholder="Add a description"
              onChange={(e) => setDescription(e.target.value)}
            />
          </div>

          <p
            className={`text-14 mt-10 text-danger`}
            hidden={errorMessage === null}
          >
            {errorMessage}
          </p>

          <div className="bx--row mt-60">
            <div className="bx--col-md-3">
              <CustomButton
                classes="outline danger no-border ml-10 no-hover font-bold"
                disabled={loading}
                onClick={cancelAction}
              >
                CANCEL
              </CustomButton>
            </div>
            <div className="bx--col-md-5">
              <CustomButton
                classes="primary f-width font-bold"
                onClick={paymentToRevise ? onRevisePayment : submitPayment}
                disabled={
                  loading ||
                  !amount ||
                  !creditAccount ||
                  !debitAccount ||
                  !paymentMode ||
                  !receivedFrom
                }
              >
                {btnText}

                <div hidden={btnText != null}>
                  <div className="d-flex-center">
                    <Loading
                      withOverlay={false}
                      small={true}
                      description="Submitting payment..."
                    />
                  </div>
                </div>
              </CustomButton>
            </div>

            <p
              className={`text-14 mt-10 text-danger`}
              hidden={errorMessage === null}
            >
              {errorMessage}
            </p>
          </div>
        </Form>
      </div>
    </Modal>
  );
};

export default ReceiveOtherPaymentModal;
